import { Box, Skeleton, Typography } from "@mui/material";
import { shuffle } from "lodash-es";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { GradientStar } from "../../styles/icons/gradient-star";
import type { DialogController } from "../../utils/hooks/use-dialog";
import { useDialog } from "../../utils/hooks/use-dialog";
import { useAccountLimitsContext } from "../context/account-limit-context";
import { Carousel } from "../discover/carousel/carousel";
import { SeverityPill } from "../generic/components/severity-pill";
import { useUpsellServices } from "../generic/hooks/use-upsell-services";
import { usePageContentMx } from "../layout/components/page-content";
import { AdditionalServicesModal } from "./additional-services-modal";
import type { AdditionalServicesType, PossibleUpsellServices } from "./type";

const allServiceItems = [
  {
    key: "sms",
    name: "SMS",
    title: "SMS - A Quicker Way to Connect with Employees",
    description: "Simplify your communication",
    image: "https://images.changeengine.com/sms1.webp",
    service: "sms" as const,
    serviceEnabled: "enable_twilio" as const,
  },
  {
    key: "listen",
    name: "Listen",
    title: "Listen More, Act Faster - Surveys That Drive Results",
    description: "Find out about ChangeEngine Listen",
    image: "https://images.changeengine.com/listen-modal-2-header.webp",
    service: "listen" as const,
    serviceEnabled: "enable_listen" as const,
  },
  {
    key: "swag",
    name: "Swag",
    title: "Celebrate With Impact & Make Memories Stick!",
    description: "Explore the swag magic",
    image: "https://images.changeengine.com/swag2.webp",
    service: "swag" as const,
    serviceEnabled: "enable_swag" as const,
  },
  {
    key: "egifting",
    name: "E-Gifting",
    title: "The Fastest Way to Make Recognition Unforgettable",
    description: "Upgrade your recognition game",
    image: "https://images.changeengine.com/giftcards.webp",
    service: "egifting" as const,
    serviceEnabled: "enable_e_gifting" as const,
  },
  {
    key: "videography",
    name: "Videography",
    title: "Bring Your Culture to Life Through Video!",
    description: "Complete the experience with video",
    image: "https://images.changeengine.com/videography2.webp",
    service: "videography" as const,
    serviceEnabled: "enable_videography" as const,
  },
  {
    key: "designservices",
    name: "Design Services",
    title: "Fuel Your Vision With Design Firepower",
    description: "Supercharge your visuals",
    image: "https://images.changeengine.com/designservices2.webp",
    service: "designservices" as const,
    serviceEnabled: "enable_design_services" as const,
  },
  {
    key: "pages",
    name: "Pages",
    title: "Create Pages That Live Beyond the Intranet",
    description: "Build secure, flexible landing pages",
    image: "https://images.changeengine.com/pages.webp",
    service: "pages" as const,
  },
  {
    key: "ats",
    name: "ATS",
    title: "Communicate With Candidates",
    description: "Connect ChangeEngine to your ATS",
    image: "https://images.changeengine.com/ats.webp",
    service: "ats" as const,
  },
  {
    key: "p2p",
    name: "Peer-to-Peer Recognition",
    title: "Enable Peer Recognition",
    description: "A lightweight way to celebrate great work",
    image: "https://images.changeengine.com/p2p.svg",
    service: "p2p" as const,
  },
  {
    key: "smstwoway",
    name: "Two-Way SMS",
    title: "Reach People - and Hear Back",
    description: "Two-way SMS for frontline teams",
    image: "https://images.changeengine.com/smstwoway.webp",
    service: "sms_two_way" as const,
    serviceEnabled: "enable_sms_two_way" as const,
  },
  {
    key: "knowledgehub",
    name: "Knowledge Hub",
    title: "Answer Policy Questions - Automatically",
    description: "An AI first line of support for HR",
    image: "https://images.changeengine.com/knowledgehub.webp",
    service: "knowledge_hub" as const,
  },
];

export const AdditionalServicesCarousel: FC<{ newPage: boolean }> = (newPage) => {
  const dialog = useDialog<{ service: AdditionalServicesType }>();
  const { loading, data: upsoldServices } = useUpsellServices();
  const { listen_entitlement, hide_additional_services_upsell } = useAccountLimitsContext();
  const pageContentMx = usePageContentMx();

  const ready = !loading && upsoldServices && listen_entitlement;
  const shuffledItems = useMemo(() => {
    const serviceEnabled = (service: PossibleUpsellServices | undefined): boolean => {
      if (!service || !upsoldServices) return false;
      return upsoldServices[service] ?? false;
    };
    if (!ready) return undefined;
    const items = allServiceItems.filter((item) => {
      if (item.key === "listen") {
        return listen_entitlement === "Disabled" && !serviceEnabled(item.serviceEnabled);
      }
      return !serviceEnabled(item.serviceEnabled);
    });
    return shuffle(items);
  }, [listen_entitlement, ready, upsoldServices]);

  if (hide_additional_services_upsell) return null;
  if (ready && shuffledItems?.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: newPage ? null : "background.default" }}>
      <Box
        sx={{
          pb: 4,
          pt: 4,
          mx: pageContentMx,
        }}
      >
        <>
          <Box sx={{ pb: 2 }}>
            <Typography fontWeight={700} variant="h6" display="flex" gap={1} color="text.primary">
              <GradientStar />
              Do something special today
            </Typography>
          </Box>
          {ready && shuffledItems && (
            <Carousel
              items={shuffledItems}
              gap={2}
              Item={({ value: props }) => (
                <ServiceCard
                  {...props}
                  data-analytics-id={`homepage-services-carousel-${props.key}`}
                  key={props.key}
                  dialog={dialog}
                />
              )}
              sx={(theme) => ({
                "--carousel-navigation-button-width": theme.spacing(6),
              })}
              itemWidth="24%"
              itemMinWidth="258px"
            />
          )}
          {!ready && <SkeletonCarousel />}
        </>
      </Box>
      <AdditionalServicesModal dialog={dialog} />
    </Box>
  );
};

const ServiceCard: FC<{
  name: string;
  title: string;
  description: string;
  service: AdditionalServicesType;
  image: string;
  "data-analytics-id"?: string;
  dialog: DialogController<{
    service: AdditionalServicesType;
  }>;
}> = ({ name, title, description, service, image, "data-analytics-id": analyticsId, dialog }) => {
  const handleClick = useCallback(() => {
    dialog.handleOpen({ service });
  }, [dialog, service]);

  return (
    <Box
      data-analytics-id={analyticsId}
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        minWidth: "258px",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 2,
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 2,
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          height: "160px",
        }}
      >
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
          <SeverityPill color="primary">{name}</SeverityPill>
        </Box>
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />
      </Box>
      <Box
        sx={{
          background: "linear-gradient(to right bottom, #FFDF00, #FFB300)",
          px: 2,
          py: 2,
          height: "120px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="body1" fontWeight="bold" color="black" lineHeight={1.4}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <Typography variant="body2" color="black" fontSize="0.79rem">
            {description}
          </Typography>
          <Typography variant="body2" color="black" fontWeight={500}>
            ›
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SkeletonCarousel: FC = () => {
  return (
    <Carousel
      items={Array.from({ length: 4 }).map((_, index) => {
        return { key: index.toString() };
      })}
      gap={2}
      Item={({ value: props }) => <SkeletonCard key={props.key} />}
      sx={(theme) => ({
        "--carousel-navigation-button-width": theme.spacing(6),
      })}
      itemWidth="24%"
      itemMinWidth="258px"
    />
  );
};

const SkeletonCard: FC = () => {
  return (
    <Box
      sx={{
        minWidth: "258px",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 2,
        backgroundColor: "white",
      }}
    >
      <Box sx={{ position: "relative", height: "160px" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ transform: "none" }} />
        <Skeleton
          variant="rounded"
          width={72}
          height={24}
          sx={{ position: "absolute", top: 12, left: 12, borderRadius: 999, transform: "none" }}
        />
      </Box>
      <Box
        sx={{
          px: 2,
          py: 2,
          height: "120px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Skeleton variant="text" width="85%" sx={{ transform: "none" }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width="55%" sx={{ transform: "none" }} />
        </Box>
      </Box>
    </Box>
  );
};
